import { $createTextNode, $getNodeByKey } from 'lexical';

// New working cursor restoration functions (extracted from MatchingEntityHighlightPlugin)
function getTextOffsetInParagraph(paragraph, targetNode, offset) {
  const children = paragraph.getChildren();

  // If the target node is the paragraph itself, calculate position based on offset
  if (targetNode.getKey() === paragraph.getKey()) {
    // For paragraph-level selection, convert the offset to text position
    let textOffset = 0;
    let childIndex = 0;

    while (childIndex < offset && childIndex < children.length) {
      textOffset += children[childIndex].getTextContent().length;
      childIndex += 1;
    }

    return textOffset;
  }

  // Find the target node among children
  const targetIndex = children.findIndex((child) => child.getKey() === targetNode.getKey());
  if (targetIndex !== -1) {
    const textOffsetBeforeTarget = children
      .slice(0, targetIndex)
      .reduce((sum, child) => sum + child.getTextContent().length, 0);
    return textOffsetBeforeTarget + offset;
  }

  // If we can't find the node, it might be nested deeper
  // Try to find it by walking the tree
  let currentOffset = 0;
  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];

    if (child.getKey() === targetNode.getKey()) {
      return currentOffset + offset;
    }

    // Check if targetNode is a descendant of this child
    if (isDescendantOf(targetNode, child)) {
      const nestedOffset = getTextOffsetInNode(child, targetNode, offset);
      return currentOffset + nestedOffset;
    }

    currentOffset += child.getTextContent().length;
  }

  return currentOffset; // fallback to end
}

function isDescendantOf(node, potentialAncestor) {
  let current = node.getParent();
  while (current) {
    if (current.getKey() === potentialAncestor.getKey()) {
      return true;
    }
    current = current.getParent();
  }
  return false;
}

function getTextOffsetInNode(node, targetNode, offset) {
  if (node.getKey() === targetNode.getKey()) {
    return offset;
  }

  if (node.getType() === 'text' || node.getType() === 'entity' || node.getType() === 'term') {
    return 0;
  }

  // For other node types, calculate offset within children
  const children = node.getChildren();
  let currentOffset = 0;

  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];

    if (child.getKey() === targetNode.getKey()) {
      return currentOffset + offset;
    }

    if (isDescendantOf(targetNode, child)) {
      return currentOffset + getTextOffsetInNode(child, targetNode, offset);
    }

    currentOffset += child.getTextContent().length;
  }

  return currentOffset;
}

function findPositionInParagraph(paragraph, textOffset) {
  let currentOffset = 0;
  const children = paragraph.getChildren();

  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];
    const childTextLength = child.getTextContent().length;

    // Special handling for empty text nodes at the beginning
    if (textOffset === 0 && i === 0 && child.getType() === 'text') {
      return {
        node: child,
        offset: 0
      };
    }

    if (currentOffset + childTextLength >= textOffset) {
      const relativeOffset = textOffset - currentOffset;

      // If this is an entity or term node, we can't place cursor inside it
      if (child.getType() === 'entity' || child.getType() === 'term') {
        // If we're at the exact boundary of a special node, be smarter about placement
        if (relativeOffset === 0) {
          // Check if the previous child is a linebreak
          if (i > 0) {
            const prevChild = children[i - 1];

            if (prevChild.getType() === 'linebreak') {
              // For linebreaks, we want to place cursor right after the linebreak
              const newTextNode = $createTextNode('');
              prevChild.insertAfter(newTextNode);
              return {
                node: newTextNode,
                offset: 0
              };
            }
            if (prevChild.getType() === 'text') {
              return {
                node: prevChild,
                offset: prevChild.getTextContent().length
              };
            }
          }
          // If no previous text node, create one before
          const nextTextNode = findOrCreateAdjacentTextNode(paragraph, i, 'before');
          if (nextTextNode) {
            return {
              node: nextTextNode,
              offset: nextTextNode.getTextContent().length
            };
          }
        } else if (relativeOffset === childTextLength) {
          // Cursor at exact end of special node - place after it
          if (i < children.length - 1) {
            const nextChild = children[i + 1];

            if (nextChild.getType() === 'text') {
              return {
                node: nextChild,
                offset: 0
              };
            }
            if (nextChild.getType() === 'entity' || nextChild.getType() === 'term') {
              // Create a text node between the two special nodes for cursor placement
              const newTextNode = $createTextNode('');
              child.insertAfter(newTextNode);
              return {
                node: newTextNode,
                offset: 0
              };
            }
          }
          // If no next text node and we're at the end, create one after only if it's truly the end
          const nextTextNode = findOrCreateAdjacentTextNode(paragraph, i, 'after');
          if (nextTextNode) {
            return {
              node: nextTextNode,
              offset: 0
            };
          }
        } else if (relativeOffset <= childTextLength / 2) {
          // Try to place cursor at the end of previous text node
          if (i > 0) {
            const prevChild = children[i - 1];

            if (prevChild.getType() === 'linebreak') {
              // For linebreaks, we want to place cursor right after the linebreak
              const newTextNode = $createTextNode('');
              prevChild.insertAfter(newTextNode);
              return {
                node: newTextNode,
                offset: 0
              };
            }
            if (prevChild.getType() === 'text') {
              return {
                node: prevChild,
                offset: prevChild.getTextContent().length
              };
            }
          }
          // If no previous text node, create or find next text node
          const nextTextNode = findOrCreateAdjacentTextNode(paragraph, i, 'before');
          if (nextTextNode) {
            return {
              node: nextTextNode,
              offset: nextTextNode.getTextContent().length
            };
          }
        } else {
          // Place cursor after the special node
          if (i < children.length - 1) {
            const nextChild = children[i + 1];
            if (nextChild.getType() === 'text') {
              return {
                node: nextChild,
                offset: 0
              };
            }
          }
          // If no next text node, create one
          const nextTextNode = findOrCreateAdjacentTextNode(paragraph, i, 'after');
          if (nextTextNode) {
            return {
              node: nextTextNode,
              offset: 0
            };
          }
        }
      } else if (child.getType() === 'text') {
        // Normal text node - safe to place cursor
        return {
          node: child,
          offset: Math.min(relativeOffset, childTextLength)
        };
      } else if (child.getType() === 'linebreak') {
        // If cursor should be positioned within or after a linebreak
        if (relativeOffset === 0) {
          // Cursor at start of linebreak - place before it
          if (i > 0) {
            const prevChild = children[i - 1];
            if (prevChild.getType() === 'text') {
              return {
                node: prevChild,
                offset: prevChild.getTextContent().length
              };
            }
          }
          // Create text node before linebreak
          const newTextNode = $createTextNode('');
          child.insertBefore(newTextNode);
          return {
            node: newTextNode,
            offset: 0
          };
        }
        // Cursor after linebreak - create or find text node after it
        if (i < children.length - 1) {
          const nextChild = children[i + 1];
          if (nextChild.getType() === 'text') {
            return {
              node: nextChild,
              offset: 0
            };
          }
        }
        // Create text node after linebreak
        const newTextNode = $createTextNode('');
        child.insertAfter(newTextNode);
        return {
          node: newTextNode,
          offset: 0
        };
      }
    }

    currentOffset += childTextLength;
  }

  // If we're past the end, place cursor at the end of the last text node
  for (let i = children.length - 1; i >= 0; i -= 1) {
    const child = children[i];
    if (child.getType() === 'text') {
      return {
        node: child,
        offset: child.getTextContent().length
      };
    }
  }

  // If no text nodes exist, return null to signal that paragraph.selectEnd() should be used
  return null;
}

function findOrCreateAdjacentTextNode(paragraph, specialNodeIndex, direction) {
  const children = paragraph.getChildren();

  if (direction === 'before') {
    // Look for text node before the special node
    for (let i = specialNodeIndex - 1; i >= 0; i -= 1) {
      if (children[i].getType() === 'text') {
        return children[i];
      }
      // If we encounter a linebreak, create a text node after it instead of before the special node
      if (children[i].getType() === 'linebreak') {
        const newTextNode = $createTextNode('');
        children[i].insertAfter(newTextNode);
        return newTextNode;
      }
    }
    // Create a text node before the special node
    const newTextNode = $createTextNode('');
    if (specialNodeIndex > 0) {
      children[specialNodeIndex - 1].insertAfter(newTextNode);
    } else {
      children[specialNodeIndex].insertBefore(newTextNode);
    }
    return newTextNode;
  }
  // Look for text node after the special node
  for (let i = specialNodeIndex + 1; i < children.length; i += 1) {
    if (children[i].getType() === 'text') {
      return children[i];
    }
  }
  // Create a text node after the special node
  const newTextNode = $createTextNode('');
  if (specialNodeIndex < children.length - 1) {
    children[specialNodeIndex + 1].insertBefore(newTextNode);
  } else {
    children[specialNodeIndex].insertAfter(newTextNode);
  }
  return newTextNode;
}

// Unified function to ensure text nodes around special nodes for consistent cursor positioning
function ensureTextNodesAroundSpecialNodes(paragraph, specialNodeType) {
  const children = paragraph.getChildren();
  const specialNodes = children.filter((child) => child.getType() === specialNodeType);

  specialNodes.forEach((specialNode) => {
    const currentChildren = paragraph.getChildren();
    const nodeIndex = currentChildren.findIndex((child) => child.getKey() === specialNode.getKey());

    if (nodeIndex === -1) return; // Node not found, skip

    const prevSibling = nodeIndex > 0 ? currentChildren[nodeIndex - 1] : null;
    const nextSibling =
      nodeIndex < currentChildren.length - 1 ? currentChildren[nodeIndex + 1] : null;

    // Add text node before special node for cursor positioning
    if (!prevSibling || prevSibling.getType() !== 'text') {
      if (nodeIndex === 0) {
        // At paragraph start - create empty text node for cursor placement
        const beforeTextNode = $createTextNode('');
        specialNode.insertBefore(beforeTextNode);
      } else {
        // Check what precedes the special node to determine spacing
        const actualPrevSibling = currentChildren[nodeIndex - 1];
        const prevSiblingText = actualPrevSibling ? actualPrevSibling.getTextContent() : '';

        // Add appropriate text node based on context
        if (
          actualPrevSibling?.getType() === 'linebreak' ||
          (prevSiblingText && /\s$/.test(prevSiblingText))
        ) {
          // Previous is linebreak or ends with space - just add empty text for cursor
          const beforeTextNode = $createTextNode('');
          specialNode.insertBefore(beforeTextNode);
        } else if (
          actualPrevSibling?.getType() === 'term' ||
          actualPrevSibling?.getType() === 'entity'
        ) {
          // Previous is special node - don't auto-add space, respect user's choice
          const beforeTextNode = $createTextNode('');
          specialNode.insertBefore(beforeTextNode);
        } else {
          // Previous doesn't end with space and isn't special - add space
          const beforeTextNode = $createTextNode(' ');
          specialNode.insertBefore(beforeTextNode);
        }
      }
    }

    // Add text node after special node for cursor positioning
    if (!nextSibling || nextSibling.getType() !== 'text') {
      // Check if next node is special to avoid auto-spacing
      if (nextSibling && (nextSibling.getType() === 'term' || nextSibling.getType() === 'entity')) {
        // Next is special node - don't auto-add space, only add cursor positioning node
        const afterTextNode = $createTextNode('');
        specialNode.insertAfter(afterTextNode);
      } else {
        // Safe to add space since next node isn't special
        const afterTextNode = $createTextNode(' ');
        specialNode.insertAfter(afterTextNode);
      }
    }
  });
}

// Unified cursor positioning function for special nodes (terms/entities)
function positionCursorAroundSpecialNode(editor, node, side, nodeKey) {
  if (side === 'before') {
    const prevSibling = node.getPreviousSibling();

    if (prevSibling && prevSibling.getType() === 'linebreak') {
      // Create text node with zero-width space after linebreak for cursor positioning
      const textNode = $createTextNode('\u200B'); // Zero-width space
      prevSibling.insertAfter(textNode);

      // Use a delay to let any plugin transformations complete
      setTimeout(() => {
        editor.update(() => {
          // Re-find the text node and select it
          const updatedNode = $getNodeByKey(nodeKey);
          if (updatedNode) {
            const updatedPrevSibling = updatedNode.getPreviousSibling();
            if (updatedPrevSibling && updatedPrevSibling.getType() === 'text') {
              updatedPrevSibling.select(0, 0);
            } else {
              // The text node might have been moved or merged, try to find a text node after linebreak
              const paragraph = updatedNode.getParent();
              if (paragraph) {
                const children = paragraph.getChildren();
                const nodeIndex = children.findIndex(
                  (child) => child.getKey() === updatedNode.getKey()
                );
                if (nodeIndex > 0) {
                  const prevChild = children[nodeIndex - 1];
                  if (prevChild.getType() === 'text') {
                    prevChild.select(0, 0);
                  }
                }
              }
            }
          }
        });
      }, 250);
    } else if (prevSibling && prevSibling.getType() === 'text') {
      // Position at end of existing text node
      const textLength = prevSibling.getTextContent().length;
      prevSibling.select(textLength, textLength);
    } else {
      // Create text node before special node
      const textNode = $createTextNode('');
      node.insertBefore(textNode);
      textNode.select(0, 0);
    }
  } else {
    // side === 'after'
    const nextSibling = node.getNextSibling();

    if (nextSibling && nextSibling.getType() === 'text') {
      // Position at start of existing text node
      nextSibling.select(0, 0);
    } else {
      // Create text node after special node
      const textNode = $createTextNode('');
      node.insertAfter(textNode);
      textNode.select(0, 0);
    }
  }
}

// Function to detect logical adjacency between special nodes, ignoring insignificant text
function areSpecialNodesLogicallyAdjacent(children, nodeIndex, targetNodeType) {
  const currentNode = children[nodeIndex];
  const currentNodeType = currentNode.getType();

  // Only check adjacency for special nodes (entity/term)
  if (currentNodeType !== 'entity' && currentNodeType !== 'term') {
    return false;
  }

  // Helper to identify insignificant text nodes (empty, zero-width, whitespace-only)
  const isInsignificantTextNode = (node) => {
    if (node.getType() !== 'text') return false;
    const text = node.getTextContent();
    // Only empty or zero-width spaces are truly insignificant
    return text === '' || text === '\u200B' || /^[\u200B]*$/.test(text);
  };

  // Search backwards for target node type
  let foundTarget = false;
  let hasOnlyInsignificantSeparation = true;

  for (let i = nodeIndex - 1; i >= 0; i -= 1) {
    const node = children[i];
    const nodeType = node.getType();

    if (nodeType === targetNodeType) {
      foundTarget = true;
      break;
    }
    if (nodeType === 'entity' || nodeType === 'term') {
      break; // Found different special node, not adjacent
    }
    if (!isInsignificantTextNode(node)) {
      // Any significant text (including spaces) breaks adjacency
      hasOnlyInsignificantSeparation = false;
      break;
    }
  }

  if (foundTarget && hasOnlyInsignificantSeparation) {
    return true;
  }

  // Search forwards for target node type
  foundTarget = false;
  hasOnlyInsignificantSeparation = true;

  for (let i = nodeIndex + 1; i < children.length; i += 1) {
    const node = children[i];
    const nodeType = node.getType();

    if (nodeType === targetNodeType) {
      foundTarget = true;
      break;
    }
    if (nodeType === 'entity' || nodeType === 'term') {
      break; // Found different special node, not adjacent
    }
    if (!isInsignificantTextNode(node)) {
      // Any significant text (including spaces) breaks adjacency
      hasOnlyInsignificantSeparation = false;
      break;
    }
  }

  return foundTarget && hasOnlyInsignificantSeparation;
}

export {
  // New working cursor restoration functions
  getTextOffsetInParagraph,
  isDescendantOf,
  getTextOffsetInNode,
  findPositionInParagraph,
  findOrCreateAdjacentTextNode,
  ensureTextNodesAroundSpecialNodes,
  positionCursorAroundSpecialNode,
  areSpecialNodesLogicallyAdjacent
};
